import React, { useState, useRef, useEffect } from "react";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import styles from "./toggle.css";

export const TOGGLE_TYPES = {
  PRIMARY: "primary",
  SECONDARY: "secondary"
};

// eslint-disable-next-line kf/const-naming-convention
const False = {
  insetInlineStart: "0%",
  gridColumn: "1/2",
  borderStartStartRadius: "3px",
  borderEndStartRadius: "3px"
};

// eslint-disable-next-line kf/const-naming-convention
const True = {
  insetInlineStart: "100%",
  gridColumn: "1/2",
  borderStartEndRadius: "3px",
  borderEndEndRadius: "3px"
};

export function Toggle({
  trueValue = t`Visible`,
  falseValue = t`Hidden`,
  handleChange,
  value = false,
  className,
  type = TOGGLE_TYPES.PRIMARY,
  disabled = false
}) {
  const [checked, updateValue] = useState(value || false);
  const toggleElement = useRef();
  useEffect(function renderEleEffect() {
    let element = toggleElement.current;
    let yesElement = element.getElementsByClassName(`${styles.yes}`)[0];
    let yesWidth = yesElement.getBoundingClientRect().width;
    let noElement = element.getElementsByClassName(`${styles.no}`)[0];
    let noWidth = noElement.getBoundingClientRect().width;
    let switchElement = element.getElementsByClassName(`${styles.switch}`)[0];
    if (yesWidth > noWidth) {
      noElement.style.width = yesWidth + "px";
      switchElement.style.width = yesWidth + "px";
      noWidth = yesWidth;
    } else {
      yesElement.style.width = noWidth + "px";
      switchElement.style.width = noWidth + "px";
      yesWidth = noWidth;
    }
    element.style.width = noWidth + yesWidth + "px";
  }, []);

  function onChange(e) {
    if (disabled) return;
    updateValue(!checked);
    handleChange && handleChange(e, !checked);
  }

  useEffect(
    function onValuePropChange() {
      updateValue(value || false);
    },
    [value]
  );

  return (
    <div
      className={`${styles.toggleContainer} ${styles[className]} ${
        disabled ? styles.disabled : ""
      }`}
      tabIndex="0"
      onClick={onChange}
      ref={toggleElement}
      data-component="toggle"
      aria-checked={checked}
      aria-label={checked ? trueValue : falseValue}
    >
      <div
        className={`${styles.optionContainer} ${styles[className + "Yes"]} ${
          styles.yes
        }`}
      >
        {trueValue}
      </div>
      <div
        className={`${styles.optionContainer} ${styles[className + "No"]} ${
          styles.no
        }`}
      >
        {falseValue}
      </div>
      <div
        className={`${styles.switch} ${styles[type]}
          ${!checked ? styles.noHighlight : styles.yesHighlight}`}
        style={!checked ? True : False}
      >
        {!checked ? falseValue : trueValue}
      </div>
    </div>
  );
}

Toggle.propTypes = {
  /** True value of the toggle to be displayed */
  trueValue: PropTypes.string,
  /** False value of the toggle to be displayed */
  falseValue: PropTypes.string,
  /** Callback function on value change */
  handleChange: PropTypes.func,
  /** Selected value (true or false) */
  value: PropTypes.bool,
  /** Additional class to pass to Toggle */
  className: PropTypes.string,
  /** Type of toggle button primary or secondary */
  type: PropTypes.string,
  /** To enable disabled mode */
  disabled: PropTypes.bool
};

Toggle.defaultProps = {
  value: false,
  type: TOGGLE_TYPES.PRIMARY,
  disabled: false
};
